// Small browser utilities shared across the SDK.

export function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  // RFC4122-ish fallback.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** A monotonic-ish session clock: ms since the session started. */
export class Clock {
  readonly startedAt: number;
  private perfStart: number;
  /** Wall ms elapsed between the ORIGINAL session start and this page's start —
   *  added to every `t` so a resumed (continued) session's timeline keeps climbing
   *  across the inactivity gap instead of restarting at 0. 0 for a fresh session. */
  private baseT: number;
  /** @param resumeStartedAt epoch ms of the original session start when RESUMING a
   *  continued session; omit for a fresh session.
   *  @param floorT highest `t` the prior page reached — guarantees this page's
   *  timeline starts after it, so a backward wall-clock jump between loads can't
   *  produce overlapping timestamps (which would corrupt replay ordering). */
  constructor(resumeStartedAt?: number, floorT?: number) {
    const wallNow = Date.now();
    this.startedAt = resumeStartedAt ?? wallNow;
    this.perfStart = typeof performance !== 'undefined' ? performance.now() : 0;
    this.baseT = Math.max(0, wallNow - this.startedAt, floorT ?? 0);
  }
  /** ms since the (original) session start — the master clock `t`. */
  now(): number {
    const p = typeof performance !== 'undefined' ? performance.now() : Date.now() - this.startedAt;
    return Math.max(0, this.baseT + Math.round(p - this.perfStart));
  }
  /** wall-clock epoch ms for display (`ts`). */
  wall(): number {
    return Date.now();
  }
  /** Convert a performance-timeline timestamp (since timeOrigin) to session t. */
  fromPerf(perfTs: number): number {
    return Math.max(0, this.baseT + Math.round(perfTs - this.perfStart));
  }
}

export async function sha256Hex(input: string): Promise<string> {
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }
  // Non-crypto fallback (still one-way enough for an opaque id).
  let h = 0;
  for (let i = 0; i < input.length; i++) h = (Math.imul(31, h) + input.charCodeAt(i)) | 0;
  return (h >>> 0).toString(16);
}

export function detectDevice(): {
  type: 'desktop' | 'mobile' | 'tablet';
  os?: string;
  browser?: string;
  viewport: { w: number; h: number };
  tz?: string;
  tzOffset?: number;
} {
  const ua = navigator.userAgent;
  const viewport = { w: window.innerWidth, h: window.innerHeight };

  // The visitor's own timezone — so the player can show wall-clock times in their
  // local time, not just the viewer's. IANA name (DST-correct) plus a numeric
  // offset EAST of UTC as a fallback label. Both best-effort.
  let tz: string | undefined;
  let tzOffset: number | undefined;
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
    tzOffset = -new Date().getTimezoneOffset();
  } catch {
    /* Intl unavailable — omit timezone */
  }
  let type: 'desktop' | 'mobile' | 'tablet' = 'desktop';
  if (/iPad|Tablet/i.test(ua)) type = 'tablet';
  else if (/Mobi|Android|iPhone/i.test(ua)) type = 'mobile';

  let os: string | undefined;
  if (/Windows/i.test(ua)) os = 'Windows';
  else if (/Mac OS X|Macintosh/i.test(ua)) os = 'macOS';
  else if (/Android/i.test(ua)) os = 'Android';
  else if (/iPhone|iPad|iOS/i.test(ua)) os = 'iOS';
  else if (/Linux/i.test(ua)) os = 'Linux';

  let browser: string | undefined;
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Safari\//.test(ua)) browser = 'Safari';

  return { type, os, browser, viewport, tz, tzOffset };
}

/** Global Privacy Control / Do Not Track. */
export function privacySignalsOptOut(): boolean {
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean; doNotTrack?: string };
  if (nav.globalPrivacyControl === true) return true;
  if (nav.doNotTrack === '1' || (window as { doNotTrack?: string }).doNotTrack === '1') return true;
  return false;
}

/**
 * Is this visitor plausibly in the EU/EEA (or UK)?
 *
 * Used to decide whether consent is required by default. GDPR binds on where
 * the DATA SUBJECT is, not where the customer is, so a global default of "record
 * first" is wrong for a European visitor and a global default of "ask first"
 * needlessly breaks recording for everyone else.
 *
 * The IANA timezone is the only region signal available in the page without a
 * network call, without an IP lookup and without storing anything — which makes
 * it the only one usable *before* consent exists. It is a heuristic: a European
 * on holiday reads as local, a VPN user reads as wherever they exit. It errs
 * toward asking, and a customer who wants certainty sets requireConsent: true.
 */
const NON_EEA_EUROPE = new Set([
  'Europe/Moscow', 'Europe/Minsk', 'Europe/Istanbul', 'Europe/Kiev', 'Europe/Kyiv',
  'Europe/Volgograd', 'Europe/Samara', 'Europe/Saratov', 'Europe/Astrakhan',
  'Europe/Ulyanovsk', 'Europe/Kirov', 'Europe/Simferopol', 'Europe/Chisinau',
  'Europe/Belgrade', 'Europe/Sarajevo', 'Europe/Skopje', 'Europe/Podgorica',
  'Europe/Tirane', 'Europe/Andorra', 'Europe/Monaco', 'Europe/San_Marino',
  'Europe/Vatican',
]);
/* EEA/UK territories that do not sit under a Europe/ zone. */
const EEA_OUTLIERS = new Set([
  'Atlantic/Reykjavik', 'Atlantic/Canary', 'Atlantic/Madeira', 'Atlantic/Azores',
  'Atlantic/Faroe', 'Indian/Mayotte', 'Indian/Reunion',
  'America/Martinique', 'America/Guadeloupe', 'America/Cayenne',
  'America/Miquelon', 'America/Marigot', 'America/St_Barthelemy',
]);

export function inEeaByTimezone(): boolean {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!tz) return true; // unknown → assume protected
    if (EEA_OUTLIERS.has(tz)) return true;
    if (tz.startsWith('Europe/')) return !NON_EEA_EUROPE.has(tz);
    return false;
  } catch {
    return true; // no Intl → assume protected
  }
}
